import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  CircleDollarSign,
  ShieldCheck,
  Gift,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { RubyPublicAssistant } from "@/components/shared/RubyPublicAssistant";
import { pathnameToRubyLandingContext } from "@/lib/rubyPathnameContext";
import { protectMarketingUrl } from "@/lib/protect-marketing-url";
import reklaimLogo from "@/assets/reklaim-wordmark.png";

interface NavItem {
  label: string;
  sublabel: string;
  path: string;
  icon: LucideIcon;
  iconClassName: string;
  external?: boolean;
}

/**
 * Marketing nav cluster -- 4 product pillars surfaced in the floating bar.
 * The Tailwind responsive layer hides `sublabel` below `xl` and the entire
 * cluster below `lg`; the mobile sheet always shows both.
 *
 * Earn, Rewards, and How It Works keep Vite paths. Those paths redirect to
 * Next marketing. Protect opens the Next `/protect` page in a new tab.
 */
const NAV_ITEMS: readonly NavItem[] = [
  {
    label: "Earn",
    sublabel: "Data Rewards",
    path: "/earn",
    icon: CircleDollarSign,
    iconClassName: "text-yellow-500",
  },
  {
    label: "Protect",
    sublabel: "Your Data",
    path: protectMarketingUrl("/protect"),
    icon: ShieldCheck,
    iconClassName: "text-violet-600",
    external: true,
  },
  {
    label: "Rewards",
    sublabel: "Rewards & Offers",
    path: "/how-it-works",
    icon: Gift,
    iconClassName: "text-emerald-500",
  },
  {
    label: "How It Works",
    sublabel: "Simple & Transparent",
    path: "/how-it-works#step-by-step",
    icon: Sparkles,
    iconClassName: "text-violet-600",
  },
] as const;

export const Navbar = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  /**
   * Active-state check for a nav item path. Handles three shapes:
   *  - External URLs (`http(s)://...`): never active.
   *  - Pathname + hash (`/how-it-works#earn`): active only when both the
   *    current `location.pathname` and `location.hash` match. Without the
   *    hash gate, all three `/how-it-works#…` items would light up
   *    simultaneously whenever the user lands on `/how-it-works`.
   *  - Pathname only (`/foo`): active when `location.pathname` matches and
   *    no hash is required.
   */
  const isActive = (itemPath: string) => {
    if (itemPath.startsWith("http")) return false;
    const [itemPathname, itemHash] = itemPath.split("#");
    if (location.pathname !== itemPathname) return false;
    if (itemHash) return location.hash === `#${itemHash}`;
    return true;
  };
  const rubyContext = pathnameToRubyLandingContext(location.pathname);

  return (
    <RubyPublicAssistant context={rubyContext}>
      {(rubyApi) => (
        <nav className="fixed top-4 left-4 right-4 z-50">
          {/* Bar height: `h-16` was too tight vertically because the Ruby chat
           * trigger stacks an input pill (40px) + an "AI powered by Ruby" badge
           * (~18px) inside the bar -- only ~3px of breathing room above/below.
           * `h-20` (80px) gives the AI section ~10px top + ~10px bottom padding,
           * which matches the design feel from the REK-1501 mock. The pure-icon
           * nav items (Earn / Protect / Rewards / How It Works) absorb the
           * extra space gracefully because they centre via `items-center`. */}
          <div className="mx-auto max-w-7xl bg-card/85 backdrop-blur-md rounded-full shadow-lg border border-border/50 px-4 sm:px-6 h-20 flex items-center gap-3">
            {/* Logo (always visible) */}
            <div className="flex items-center shrink-0">
              <Link to="/" className="flex items-center" aria-label="Reklaim home">
                <img src={reklaimLogo} alt="Reklaim" className="h-6" loading="eager" />
              </Link>
            </div>

            {!isMobile && (
              <>
                {/* Middle nav cluster (≥lg). Subtitles only render at ≥xl. */}
                <div className="hidden lg:flex items-center gap-5 xl:gap-7 px-2">
                  {NAV_ITEMS.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    const inner = (
                      <>
                        <Icon
                          className={`w-5 h-5 ${item.iconClassName} transition-transform group-hover:scale-110`}
                          aria-hidden="true"
                        />
                        <div className="flex flex-col items-center leading-tight">
                          <span
                            className={
                              active
                                ? "text-sm font-semibold text-foreground"
                                : "text-sm font-medium text-foreground/85 group-hover:text-foreground transition-colors"
                            }
                          >
                            {item.label}
                          </span>
                          <span className="hidden xl:block text-[11px] text-muted-foreground">
                            {item.sublabel}
                          </span>
                        </div>
                      </>
                    );

                    if (item.external) {
                      return (
                        <a
                          key={item.label}
                          href={item.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex flex-col items-center gap-0.5"
                        >
                          {inner}
                        </a>
                      );
                    }
                    return (
                      <Link
                        key={item.label}
                        to={item.path}
                        className="group flex flex-col items-center gap-0.5"
                      >
                        {inner}
                      </Link>
                    );
                  })}
                </div>

                {/* Ruby chat trigger (≥md). Pretends to be an input but is
                 *  actually a button that opens the existing `RubyPublicAssistant`
                 *  Sheet. The "AI powered by Ruby" badge below collapses at <xl
                 *  to keep the bar a single row at narrower widths. */}
                <div className="hidden md:flex flex-1 items-center justify-center min-w-0">
                  <button
                    type="button"
                    onClick={rubyApi.open}
                    aria-label="Ask Reklaim AI"
                    className="group flex flex-col items-center gap-1 w-full max-w-md"
                  >
                    <div className="w-full flex items-center gap-2 h-10 px-4 rounded-full border-2 border-yellow-400/70 hover:border-yellow-400 bg-card/60 hover:bg-card transition-colors shadow-sm">
                      <Sparkles
                        className="w-4 h-4 text-violet-500 shrink-0"
                        aria-hidden="true"
                      />
                      <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors truncate">
                        Ask Reklaim AI anything…
                      </span>
                    </div>
                    <span className="hidden xl:inline-flex items-center gap-1 text-[11px] text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
                      <Sparkles
                        className="w-3 h-3 text-violet-500"
                        aria-hidden="true"
                      />
                      AI powered by{" "}
                      <span className="font-semibold text-violet-600">Ruby</span>
                    </span>
                  </button>
                </div>

                {/* Right slot: Login (or My Account when authenticated). The
                 * "Get Started" CTA from the design spec was intentionally
                 * dropped per REK-1501 product feedback -- the homepage hero
                 * already owns the primary signup CTA. */}
                <div className="ml-auto flex items-center shrink-0">
                  {isAuthenticated ? (
                    <Link to="/dashboard">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                      >
                        My Account
                      </Button>
                    </Link>
                  ) : (
                    <Link to="/auth?mode=login">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full"
                      >
                        Login
                      </Button>
                    </Link>
                  )}
                </div>
              </>
            )}

            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                className="ml-auto shrink-0"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={
                  mobileMenuOpen
                    ? "Close navigation menu"
                    : "Open navigation menu"
                }
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            )}
          </div>

          {isMobile && mobileMenuOpen && (
            <div className="mt-2 mx-auto max-w-7xl rounded-2xl border border-border/50 bg-card/95 backdrop-blur-md shadow-lg">
              <div className="p-6 flex flex-col gap-2">
                {NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  const inner = (
                    <>
                      <Icon
                        className={`w-5 h-5 ${item.iconClassName} shrink-0`}
                        aria-hidden="true"
                      />
                      <div className="flex flex-col">
                        <span
                          className={
                            active
                              ? "text-sm font-semibold text-foreground"
                              : "text-sm font-medium text-foreground/85"
                          }
                        >
                          {item.label}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {item.sublabel}
                        </span>
                      </div>
                    </>
                  );

                  if (item.external) {
                    return (
                      <a
                        key={item.label}
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 py-2"
                      >
                        {inner}
                      </a>
                    );
                  }
                  return (
                    <Link
                      key={item.label}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 py-2"
                    >
                      {inner}
                    </Link>
                  );
                })}

                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    rubyApi.open();
                  }}
                  aria-label="Ask Reklaim AI"
                  className="mt-2 flex items-center gap-2 px-4 py-3 rounded-full border-2 border-yellow-400/70 bg-card/60"
                >
                  <Sparkles
                    className="w-4 h-4 text-violet-500 shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-sm text-muted-foreground">
                    Ask Reklaim AI anything…
                  </span>
                </button>

                <div className="pt-4 mt-2 border-t border-border flex flex-col gap-3">
                  {isAuthenticated ? (
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block"
                    >
                      <Button className="w-full bg-[#2432C8] hover:bg-[#1d28a0] text-white">
                        My Account
                      </Button>
                    </Link>
                  ) : (
                    <Link
                      to="/auth?mode=login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block"
                    >
                      <Button className="w-full bg-[#2432C8] hover:bg-[#1d28a0] text-white">
                        Login
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}
        </nav>
      )}
    </RubyPublicAssistant>
  );
};
